import React, { useState, useCallback, useEffect, ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { Badge, BadgeCelebrationContext } from '@/hooks/useBadges';
import { BadgeEarnedCelebration } from './BadgeEarnedCelebration';

export function useBadgeCelebration() {
  const context = React.useContext(BadgeCelebrationContext);
  if (!context) {
    throw new Error('useBadgeCelebration must be used within a BadgeCelebrationProvider');
  }
  return context;
}

interface BadgeCelebrationProviderProps {
  children: ReactNode;
}

export function BadgeCelebrationProvider({ children }: BadgeCelebrationProviderProps) {
  const [celebrationQueue, setCelebrationQueue] = useState<Badge[]>([]);
  const [currentBadge, setCurrentBadge] = useState<Badge | null>(null);
  const { user } = useAuth();
  const queryClient = useQueryClient();

  // Once per session per account: claim a Founding Member badge saved to this
  // email by a pre-launch vote (guests who made their account after launch).
  useEffect(() => {
    if (!user?.id) return;
    const key = `grounded_founding_check_${user.id}`;
    try { if (sessionStorage.getItem(key)) return; sessionStorage.setItem(key, '1'); } catch { /* ignore */ }
    supabase.functions.invoke('check-badges', {
      body: { user_id: user.id, action_type: 'signup', timezone: Intl.DateTimeFormat().resolvedOptions().timeZone },
    }).then(({ data }) => {
      const earned = (data?.newly_earned ?? []) as Array<{ badge: Badge }>;
      if (earned.length === 0) return;
      queryClient.invalidateQueries({ queryKey: ['user-badges', user.id] });
      earned.forEach((e) => setCelebrationQueue((prev) => [...prev, e.badge]));
    }).catch(() => { /* best effort */ });
  }, [user?.id, queryClient]);

  // Start a queued celebration when nothing is showing.
  useEffect(() => {
    if (currentBadge || celebrationQueue.length === 0) return;
    const [first, ...rest] = celebrationQueue;
    setCurrentBadge(first);
    setCelebrationQueue(rest);
  }, [currentBadge, celebrationQueue]);

  const celebrateBadge = useCallback((badge: Badge) => {
    if (currentBadge) {
      // Queue the badge if one is already showing
      setCelebrationQueue(prev => [...prev, badge]);
    } else {
      setCurrentBadge(badge);
    }
  }, [currentBadge]);

  const handleComplete = useCallback(() => {
    if (celebrationQueue.length > 0) {
      // Show next badge in queue
      const [nextBadge, ...rest] = celebrationQueue;
      setCurrentBadge(nextBadge);
      setCelebrationQueue(rest);
    } else {
      setCurrentBadge(null);
    }
  }, [celebrationQueue]);

  return (
    <BadgeCelebrationContext.Provider value={{ celebrateBadge }}>
      {children}
      {currentBadge && (
        <BadgeEarnedCelebration 
          badge={currentBadge} 
          onComplete={handleComplete}
        />
      )}
    </BadgeCelebrationContext.Provider>
  );
}
